import { useCallback, useEffect, useState } from 'react';

import { useWindowDimensions } from 'react-native';

import { useDispatch, useSelector } from 'react-redux';

import { StackActions } from '@react-navigation/native';
import debounce from 'lodash.debounce';

import { SCREEN } from 'router/screens';
import { RootPathnames, TEMPLATE_TO_PATHNAME } from 'router/types';

import { setInitialWebPathName } from 'store/config/actions';
import { selectInitialWebPathParams } from 'store/config/selectors';

import getScreenParamsFromPathName from 'utils/getScreenParamsFromPathName';
import { navigationRef } from 'utils/navigation/navigationUtils';
import { isWeb, webDensityCoef } from 'utils/responsiveness';

const TOP_NAVIGATOR_SCREENS = [
    SCREEN.WELCOME_SCREEN,
    SCREEN.LANDING_SCREEN,
    SCREEN.LOGIN_SCREEN,
    SCREEN.FTI_TUTORIAL,
    SCREEN.PIN_SETUP,
    SCREEN.PROFILES,
    SCREEN.PROFILE_TYPE,
    SCREEN.ADD_PROFILE,
    SCREEN.PROFILE_AGE_RATING,
    SCREEN.PROFILE_AVATARS,
    SCREEN.MANAGE_PROFILE,
];

const TAB_ROUTES = Object.values(TEMPLATE_TO_PATHNAME);

const useWindowResizeRedirect = () => {
    const [isRedirected, setIsRedirected] = useState(false);
    const { width, height } = useWindowDimensions();
    const dispatch = useDispatch();

    const initialWebPathParams = useSelector(selectInitialWebPathParams);

    const handleLandingScreenRedirectDebounced = useCallback(
        debounce(async () => {
            const calculatedWidth = 1024 / webDensityCoef;
            const calculatedHeight = 500 / webDensityCoef;

            if (
                isWeb &&
                (window.innerWidth <= calculatedWidth ||
                    window.innerHeight < calculatedHeight) &&
                !isRedirected
            ) {
                dispatch(
                    setInitialWebPathName({
                        pathName: window.location.pathname.replace('/', ''),
                        query: window.location.search.replace('?', ''),
                    }),
                );

                navigationRef.current?.dispatch(
                    StackActions.replace(SCREEN.LANDING_SCREEN),
                );

                setIsRedirected(true);
            } else if (
                isWeb &&
                window.innerWidth > calculatedWidth &&
                window.innerHeight > calculatedHeight
            ) {
                if (initialWebPathParams.pathName && isRedirected) {
                    const { screen, params } =
                        getScreenParamsFromPathName(initialWebPathParams);

                    if (TOP_NAVIGATOR_SCREENS.includes(screen as SCREEN)) {
                        navigationRef.current?.dispatch(
                            StackActions.replace(screen, params),
                        );
                    } else if (screen !== SCREEN.LANDING_SCREEN) {
                        navigationRef.current?.dispatch(
                            StackActions.replace(SCREEN.ROOT, {
                                screen,
                                params:
                                    TAB_ROUTES.includes(
                                        screen.toLowerCase() as RootPathnames,
                                    ) && !params?.screen
                                        ? JSON.stringify(params)
                                        : params,
                            }),
                        );
                    }

                    dispatch(
                        setInitialWebPathName({
                            pathName: '',
                            query: undefined,
                        }),
                    );
                }

                setIsRedirected(false);
            }
        }, 200),
        [isRedirected, initialWebPathParams],
    );

    useEffect(() => {
        handleLandingScreenRedirectDebounced();

        return () => {
            handleLandingScreenRedirectDebounced.cancel();
        };
    }, [width, height]);
};

export default useWindowResizeRedirect;
